import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  type Edge,
  ReactFlowProvider,
  type Node,
  BackgroundVariant,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { TriggerNode, ActionNode, ConditionNode, WorkflowNode, AINode, HumanNode } from "./nodes/CustomNodes";
import { useGetWorkflowVersionQuery, useGetWorkflowExecutionDetailQuery } from "@/services/WorkFlowBuilderApi";
import { useSearchParams } from "react-router-dom";
import { X, Clock, CheckCircle2, XCircle, FileJson, ArrowRight } from "lucide-react";
import { clsx } from "clsx";

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  workflow: WorkflowNode,
  ai: AINode,
  human: HumanNode,
};

const initialNodes: Node[] = [];

const ExecutionDetailsPanel = ({ node, onClose }: { node: Node | null; onClose: () => void }) => {
  if (!node || !node.data.execution) return null;
  const execution = node.data.execution as any;

  return (
    <div className="absolute right-4 top-4 bottom-4 w-96 bg-white shadow-2xl rounded-xl border border-gray-200 overflow-hidden flex flex-col z-50 animate-in slide-in-from-right-10 duration-300">
      <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={clsx("w-2 h-2 rounded-full",
            execution.status === 'completed' ? "bg-green-500" :
              execution.status === 'failed' ? "bg-red-500" : "bg-blue-500"
          )} />
          <h3 className="font-bold text-gray-800">{node.data.label as string}</h3>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full text-gray-500 transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="text-xs text-gray-500 mb-1">Status</div>
            <div className={clsx("font-semibold text-sm capitalize flex items-center gap-1.5",
              execution.status === 'completed' ? "text-green-600" :
                execution.status === 'failed' ? "text-red-600" : "text-blue-600"
            )}>
              {execution.status === 'completed' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
              {execution.status}
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="text-xs text-gray-500 mb-1">Duration</div>
            <div className="font-semibold text-sm text-gray-700 flex items-center gap-1.5">
              <Clock size={14} className="text-gray-400" />
              {execution.duration_ms}ms
            </div>
          </div>
        </div>

        {/* Input Data */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <div className="p-1 bg-blue-50 text-blue-600 rounded">
              <ArrowRight size={14} />
            </div>
            Input Data
          </div>
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 overflow-hidden">
            <pre className="text-xs font-mono text-slate-600 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(execution.input_data, null, 2)}
            </pre>
          </div>
        </div>

        {/* Output Data */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <div className="p-1 bg-green-50 text-green-600 rounded">
              <FileJson size={14} />
            </div>
            Output Data
          </div>
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 overflow-hidden">
            <pre className="text-xs font-mono text-slate-600 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(execution.output_data ?? execution.error, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkflowSimulationContent = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  const [searchParams] = useSearchParams();
  const instanceId = searchParams.get("id");
  const workflowId = searchParams.get("workflow_id");
  const workflowVersionId = searchParams.get("workflow_version_id");
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Poll for updates if running? Or just fetch once. User didn't specify polling.
  const { data: workflowData, isError } = useGetWorkflowVersionQuery({ workflowId: Number(workflowId), workflowVersionId: Number(workflowVersionId), });
  // Determine if we should poll based on status
  // We need initial polling to get the status, then stop if not running
  // But if we use 'skip' effectively we can control it? No, pollingInterval is the key.

  // We'll use a state to control polling, updated by useEffect
  const [pollingRate, setPollingRate] = useState(2000);

  const { data: workflowExecutionData } = useGetWorkflowExecutionDetailQuery(Number(instanceId), {
    pollingInterval: pollingRate,
    skip: !instanceId,
  });

  useEffect(() => {
    if (workflowExecutionData) {
      if (workflowExecutionData.status === "running") {
        setPollingRate(2000);
      } else {
        setPollingRate(0);
      }
    }
  }, [workflowExecutionData]);


  const executionMap = useMemo(() => {
    if (!workflowExecutionData || !workflowExecutionData.node_executions) return {};
    const map: Record<string, any> = {};
    workflowExecutionData.node_executions.forEach((exec: any) => {
      map[exec.node_key] = exec;
    });
    return map;
  }, [workflowExecutionData]);

  useEffect(() => {
    if (isError && workflowId) {
      setShowErrorDialog(true);
    }
  }, [isError, workflowId]);

  useEffect(() => {
    if (workflowData) {
      const mappedNodes: Node[] = workflowData.nodes.map((node) => {
        const execution = executionMap[node.node_key];
        return {
          id: node.node_key,
          type: node.node_type,
          position: node.position as { x: number; y: number },
          data: {
            label: node.label,
            config: node.config,
            execution: execution // Inject execution data here
          },
        };
      });

      const mappedEdges: Edge[] = workflowData.edges.map((edge) => {
        // highlight edge if both source and target were executed
        const sourceExec = executionMap[edge.source_node_key];
        const targetExec = executionMap[edge.target_node_key];

        let isTraversed = false;
        if (sourceExec && sourceExec.status === 'completed') {
          // For condition, AI, and Human nodes, we need to check if the edge matches the output
          // sourceExec.output_data.condition_result (true/false) matches edge.edge_label ("true"/"false")
          if (sourceExec.node_type === 'condition' || sourceExec.node_type === 'ai' || sourceExec.node_type === 'human' || ((sourceExec.output_data?.condition_result !== undefined))) {
            if (String(sourceExec.output_data?.condition_result) === edge.edge_label) {
              isTraversed = true;
            }
          } else if (targetExec) {
            // Simple path: if target was executed, this edge was probably traversed
            isTraversed = true;
          }
        }

        return {
          id: `e${edge.source_node_key}-${edge.target_node_key}`,
          source: edge.source_node_key,
          target: edge.target_node_key,
          label: edge.edge_label,
          sourceHandle: edge.edge_label === "true" || edge.edge_label === "false" ? edge.edge_label : undefined,
          animated: isTraversed,
          style: isTraversed ? { stroke: '#22c55e', strokeWidth: 2 } : { stroke: '#cbd5e1' },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isTraversed ? '#22c55e' : '#cbd5e1',
          },
        };
      });

      setNodes(mappedNodes);
      setEdges(mappedEdges);
    }
  }, [workflowData, executionMap, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  return (
    <>
      <div className="flex h-[calc(100vh-64px)] w-full flex-col relative">
        <div className="flex flex-grow h-full">
          <div className="flex-grow h-full" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              nodeTypes={nodeTypes}
              fitView
              nodesDraggable={false}
            >
              <Controls />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>
          </div>
        </div>

        {/* Execution Details Sidebar */}
        <ExecutionDetailsPanel
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
        />
      </div>
    </>
  );
};

export const WorkflowSimulation = () => {
  return (
    <ReactFlowProvider>
      <WorkflowSimulationContent />
    </ReactFlowProvider>
  );
};
